/**
 * ShapeVisual - Pure visual component for shape blocks
 *
 * Renders SVG shapes (rectangle, line, arrow) based on block properties.
 * Styling controlled via property panel in editor, not inline editing.
 */

import type { BlockVisualProps } from '../../Types'

export function ShapeVisual({ block }: BlockVisualProps) {
	// Type guard
	if (block.type !== 'shape') return null

	const props = block.properties as {
		shapeType?: 'rectangle' | 'line' | 'arrow'
		strokeColor?: string
		fillColor?: string
		strokeWidth?: number
	}

	const { shapeType, strokeColor, fillColor, strokeWidth = 2 } = props

	const style = {
		stroke: strokeColor ?? 'var(--foreground)',
		fill: fillColor ?? 'transparent',
		strokeWidth,
	}

	switch (shapeType) {
		case 'rectangle':
			return (
				<svg width="100%" height="100%" style={{ display: 'block' }}>
					<rect x="0" y="0" width="100%" height="100%" {...style} />
				</svg>
			)

		case 'line':
			return (
				<svg width="100%" height="100%" style={{ display: 'block' }}>
					<line x1="0" y1="50%" x2="100%" y2="50%" {...style} />
				</svg>
			)

		case 'arrow':
			return (
				<svg width="100%" height="100%" style={{ display: 'block' }}>
					<defs>
						<marker
							id={`arrow-${block.id}`}
							markerWidth="10"
							markerHeight="10"
							refX="9"
							refY="3"
							orient="auto"
						>
							<path d="M0,0 L0,6 L9,3 z" fill={strokeColor ?? 'var(--foreground)'} />
						</marker>
					</defs>
					<line
						x1="0"
						y1="50%"
						x2="100%"
						y2="50%"
						{...style}
						markerEnd={`url(#arrow-${block.id})`}
					/>
				</svg>
			)

		default:
			return (
				<div
					style={{
						padding: '1rem',
						color: 'var(--muted-foreground)',
						fontSize: '0.875rem',
					}}
				>
					Unknown shape type: {shapeType}
				</div>
			)
	}
}
